/**
 * SettingsPage — sekcje: Profil, Rodzina, Portfele, Kategorie, Wygląd, Wyloguj.
 * Wallets/Categories management = osobne modale (Faza 6.5 — full CRUD).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../components/Icons';
import { useTheme } from '../lib/ThemeProvider';
import { useAuthStore } from '../features/auth/authStore';
import { useWalletsStore } from '../features/wallets/walletsStore';
import { useCategoriesStore } from '../features/categories/categoriesStore';
import { signOut as signOutUser, leaveFamily, deleteAccount } from '../features/auth/api';
import { hardReload } from '../lib/hardReload';
import { formatInviteCode, copyToClipboard, safeColor } from '../lib/utils';
import { showToast } from '../components/ui/Toast';
import { WalletEditModal } from '../components/wallets/WalletEditModal';
import type { Wallet } from '../features/wallets/walletsStore';

export function SettingsPage() {
  const { theme, toggle } = useTheme();
  const user = useAuthStore((s) => s.user);
  const family = useAuthStore((s) => s.family);
  const wallets = useWalletsStore((s) => s.wallets);
  const categories = useCategoriesStore((s) => s.categories);
  const [editWallet, setEditWallet] = useState<Wallet | 'new' | null>(null);

  const handleLogout = async () => {
    if (!window.confirm('Wylogować się?')) return;
    try {
      await signOutUser();
    } catch {
      showToast('Wylogowano', 'info');
    }
  };

  const handleCopyCode = async () => {
    if (!family?.inviteCode) return;
    await copyToClipboard(family.inviteCode);
    showToast('Skopiowano kod zaproszenia', 'success');
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Ustawienia</h1>

      {/* Profil */}
      <Section title="Profil" icon={<Icons.Profile size={18} />}>
        <Row label="Imię" value={user?.name ?? '—'} />
        <Row label="Email" value={user?.email ?? '—'} />
      </Section>

      {/* Rodzina */}
      <Section title="Rodzina" icon={<Icons.Users size={18} />}>
        <Row label="Nazwa" value={family?.name ?? '—'} />
        <Row label="Członkowie" value={String(Object.keys(family?.members ?? {}).length)} />
        {family?.inviteCode && (
          <button
            type="button"
            onClick={handleCopyCode}
            className="w-full mt-2 px-3.5 py-2.5 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 text-sky-700 dark:text-sky-300 text-sm font-semibold flex items-center justify-between hover:bg-sky-100 dark:hover:bg-sky-500/20 transition"
          >
            <span className="flex items-center gap-2">
              <Icons.Copy size={14} />
              Kod zaproszenia
            </span>
            <span className="font-mono tabular-nums">{formatInviteCode(family.inviteCode)}</span>
          </button>
        )}
      </Section>

      {/* Portfele */}
      <Section
        title="Portfele"
        icon={<Icons.Wallet size={18} />}
        action={
          <button
            type="button"
            onClick={() => setEditWallet('new')}
            className="text-xs font-semibold text-sky-600 dark:text-sky-300 flex items-center gap-1"
          >
            <Icons.Plus size={14} /> Dodaj
          </button>
        }
      >
        {wallets.length === 0 ? (
          <p className="text-sm text-slate-400">Brak portfeli</p>
        ) : (
          wallets.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setEditWallet(w)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition text-left"
            >
              <span className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: safeColor(w.color) }} aria-hidden="true" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{w.name}</span>
                {w.type === 'shared' && (
                  <span className="text-[9px] uppercase tracking-wide font-bold text-slate-400">Wspólny</span>
                )}
              </span>
              <Icons.ChevronRight size={16} className="text-slate-300 dark:text-slate-600" />
            </button>
          ))
        )}
      </Section>

      {/* Kategorie */}
      <Section
        title="Kategorie"
        icon={<Icons.ChartPie size={18} />}
        action={
          <Link to="/categories" className="text-xs font-semibold text-sky-600 dark:text-sky-300 flex items-center gap-1">
            Zarządzaj <Icons.ChevronRight size={14} />
          </Link>
        }
      >
        <Row label="Wydatków" value={String(categories.filter((c) => c.type === 'expense').length)} />
        <Row label="Przychodów" value={String(categories.filter((c) => c.type === 'income').length)} />
      </Section>

      {/* Aplikacja */}
      <Section title="Aplikacja" icon={<Icons.Refresh size={18} />}>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm('Wymusić świeżą wersję? Apka się przeładuje, dane są w chmurze — nic nie tracisz.')) return;
            showToast('Czyszczę cache...', 'info');
            await hardReload();
          }}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition text-left"
        >
          <span className="text-sm text-slate-700 dark:text-slate-200">Wymuś świeżą wersję</span>
          <Icons.Refresh size={16} className="text-slate-400" />
        </button>
        <p className="px-3 text-[11px] text-slate-400 leading-snug">
          Użyj jeśli po update banner nie pomógł — wyczyści cache, service worker i przeładuje apkę.
        </p>
      </Section>

      {/* Wygląd */}
      <Section title="Wygląd" icon={theme === 'dark' ? <Icons.Moon size={18} /> : <Icons.Sun size={18} />}>
        <button
          type="button"
          onClick={toggle}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition"
        >
          <span className="text-sm text-slate-700 dark:text-slate-200">Tryb ciemny</span>
          <span className={`relative inline-block w-11 h-6 rounded-full transition-colors ${theme === 'dark' ? 'bg-sky-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${theme === 'dark' ? 'translate-x-5' : ''}`} />
          </span>
        </button>
      </Section>

      {/* Wyloguj */}
      <button
        type="button"
        onClick={handleLogout}
        className="w-full px-4 py-3.5 rounded-card bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold flex items-center justify-center gap-2 shadow-card hover:bg-rose-50 dark:hover:bg-rose-500/10 transition"
      >
        <Icons.LogOut size={16} />
        Wyloguj się
      </button>

      {/* Strefa zagrożenia */}
      <details className="bg-white dark:bg-slate-800 rounded-card border border-rose-200/50 dark:border-rose-500/20 shadow-card">
        <summary className="px-4 py-3 cursor-pointer text-sm font-bold text-rose-600 dark:text-rose-400 flex items-center gap-2 select-none">
          <Icons.Alert size={16} />
          Strefa zagrożenia
        </summary>
        <div className="px-4 pb-4 space-y-2">
          {family && (
            <button
              type="button"
              onClick={async () => {
                if (!user || !family) return;
                if (!window.confirm(`Opuścić rodzinę "${family.name}"? Twoje transakcje historyczne zostaną zachowane, ale stracisz dostęp.`)) return;
                try {
                  await leaveFamily(user.uid, family.id);
                  showToast('Opuszczono rodzinę', 'info');
                  await signOutUser();
                } catch (e) {
                  showToast(e instanceof Error ? e.message : 'Nie udało się opuścić rodziny', 'error');
                }
              }}
              className="w-full px-3.5 py-2.5 rounded-xl border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm font-semibold hover:bg-amber-50 dark:hover:bg-amber-500/10 transition flex items-center justify-between"
            >
              <span>Opuść rodzinę</span>
              <Icons.ChevronRight size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm('Trwale usunąć konto? Operacja nieodwracalna. Dane rodziny zachowane dla pozostałych członków.')) return;
              try {
                await deleteAccount();
                showToast('Konto usunięte', 'info');
              } catch (e) {
                const code = (e as { code?: string })?.code;
                if (code === 'auth/requires-recent-login') {
                  showToast('Zaloguj się ponownie i spróbuj jeszcze raz', 'warn');
                  await signOutUser();
                } else {
                  showToast(e instanceof Error ? e.message : 'Nie udało się usunąć konta', 'error');
                }
              }
            }}
            className="w-full px-3.5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition flex items-center justify-between"
          >
            <span>Usuń konto</span>
            <Icons.Trash size={14} />
          </button>
        </div>
      </details>

      <p className="text-center text-[11px] text-slate-400 dark:text-slate-500">
        Rodzinka Finance · v2.0 · Zarządzanie budżetem rodzinnym
      </p>

      {editWallet && (
        <WalletEditModal
          wallet={editWallet === 'new' ? null : editWallet}
          onClose={() => setEditWallet(null)}
        />
      )}
    </div>
  );
}

function Section({ title, icon, action, children }: { title: string; icon?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
          <span className="text-slate-400">{icon}</span>
          {title}
        </h2>
        {action}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-medium text-slate-800 dark:text-slate-100">{value}</span>
    </div>
  );
}
